import { useAuth } from "../../../contexts/AuthProvider";
import { useChosenDepartment } from "../../../contexts/ChosenDepartment/hooks";
import { FEATURE_FLAGS } from "../../../contexts/FeatureFlagsProvider";
import { useFeatureFlag } from "../../../hooks/useOpenFeatureFlag";

export type CareersAccess = {
	/** The caller may use Careers in the chosen department. Always false while `loading`. */
	allowed: boolean;
	/**
	 * The answer is not known yet. `hiring_ihh` is department-scoped, and on a cold load the chosen
	 * department is picked in an effect AFTER the first render — until then `FeatureFlagsProvider` has no
	 * department to ask about and hands out an empty flag map, which reads as "off" but means "unknown".
	 */
	loading: boolean;
};

/**
 * Whether the caller gets Careers: the `hiring_ihh` flag is on for the caller in the chosen department, and the
 * department is theirs (a member of it) or they are support. No role, view or admin check — who gets Careers is
 * decided in LaunchDarkly's targeting for the flag. It is the rule the backend enforces on every `/careers` route
 * (the membership half is its normal tenancy check), so the tab, the route guard and the root redirect can never
 * offer a page whose every request would 403.
 *
 * The flag does not add anyone to the department picker: to reach the Careers department at all, a user still has
 * to be a member of it (or support). The chosen department normally comes from that picker, so the membership
 * check only turns away a stale choice.
 */
export const useCareersAccess = (): CareersAccess => {
	const { userInfo } = useAuth();
	const { chosenDepartment } = useChosenDepartment();
	const isFlagOn = useFeatureFlag(FEATURE_FLAGS.HiringIhh);

	// A user with no department never gets one chosen, so there is nothing to wait for — just no access.
	// Optional chaining as in `ChoseDepartmentProvider`: userInfo can still be the pre-fetch placeholder.
	const hasDepartments = !!userInfo.departments?.length;
	if (chosenDepartment.department_id === "") return { allowed: false, loading: hasDepartments };
	const isMember = !!userInfo.departments?.some((department) => department.department_id === chosenDepartment.department_id);
	return { allowed: isFlagOn && (isMember || userInfo.support), loading: false };
};
